import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Check, ChevronLeft } from 'lucide-react';

export default function WeeklyUpdateForm() {
  const { divId: paramDivId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const divId = paramDivId || user?.division_id;

  const [division, setDivision] = useState(null);
  const [quantitative, setQuantitative] = useState({});
  const [qualitative, setQualitative] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [prefill, setPrefill] = useState(null);

  useEffect(() => {
    if (!divId) return;
    Promise.all([
      api.get(`/divisions/${divId}`),
      api.get(`/divisions/${divId}/latest-update`),
      api.get(`/divisions/${divId}/weekly-prefill`).catch(() => ({ data: null })),
    ]).then(([divRes, latestRes, prefillRes]) => {
      setDivision(divRes.data);
      const pf = prefillRes.data;
      setPrefill(pf);
      if (latestRes.data) {
        setQuantitative(latestRes.data.quantitative || {});
        setQualitative(latestRes.data.qualitative || {});
      } else if (pf?.has_daily_data && pf.prefill_quantitative) {
        // Pre-fill from daily sales if no existing update
        setQuantitative(prev => ({ ...prev, ...pf.prefill_quantitative }));
      }
    }).finally(() => setLoading(false));
  }, [divId]);

  const handleQuantChange = (key, value) => {
    setQuantitative(prev => ({ ...prev, [key]: value }));
  };

  const handleQualChange = (key, value) => {
    setQualitative(prev => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post(`/divisions/${divId}/updates`, { quantitative, qualitative });
      setSubmitted(true);
      setTimeout(() => navigate(`/division/${divId}`), 1500);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to submit update');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#2A7B88] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="flex flex-col items-center justify-center py-20 fade-in" data-testid="update-success">
        <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: '#ECFDF5' }}>
          <Check size={32} style={{ color: '#2D8B5E' }} />
        </div>
        <h2 className="text-lg font-bold" style={{ color: '#1B2A4A' }}>Update Submitted!</h2>
        <p className="text-sm mt-1" style={{ color: '#6B7280' }}>Redirecting to division view...</p>
      </div>
    );
  }

  if (!division) return <p className="text-center py-10">Division not found</p>;

  const qFields = division.quantitative_fields || [];
  const qualFields = division.qualitative_fields || [];

  return (
    <div className="max-w-2xl mx-auto space-y-5 fade-in" data-testid="weekly-update-form">
      <button onClick={() => navigate(`/division/${divId}`)} className="flex items-center gap-1 text-sm" style={{ color: '#2A7B88' }}
        data-testid="back-to-division">
        <ChevronLeft size={16} /> Back to {division.name}
      </button>

      <div className="bg-white rounded-lg border p-5" style={{ borderColor: '#E5E1DC', borderTopWidth: '3px', borderTopColor: division.color }}>
        <h2 className="text-lg font-bold mb-1" style={{ color: '#1B2A4A' }}>Weekly Update</h2>
        <p className="text-sm mb-5" style={{ color: '#6B7280' }}>
          {division.name} &middot; Week of {new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
        </p>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Daily sales pre-fill banner */}
          {prefill?.has_daily_data && (
            <div className="rounded-md p-3 border" style={{ backgroundColor: '#F0FDF4', borderColor: '#BBF7D0' }} data-testid="prefill-banner">
              <p className="text-xs font-semibold mb-1" style={{ color: '#2D8B5E' }}>
                Pre-filled from {prefill.entry_count} daily entries this week
              </p>
              <p className="text-xs" style={{ color: '#6B7280' }}>
                Week total: <strong style={{ color: '#1B2A4A' }}>{Number(prefill.total).toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}</strong>
                {prefill.by_outlet?.length > 0 && (
                  <span className="ml-2">({prefill.by_outlet.map(o => `${o.outlet}: ${Number(o.total).toLocaleString('en-IN')}`).join(' | ')})</span>
                )}
              </p>
              <p className="text-[10px] mt-1" style={{ color: '#6B7280' }}>Review the numbers below, adjust if needed, and add your commentary.</p>
            </div>
          )}

          {/* Quantitative Fields */}
          <div>
            <h3 className="text-sm font-semibold mb-3 pb-2 border-b" style={{ color: '#1B2A4A', borderColor: '#E5E1DC' }}>
              Numbers
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {qFields.map(field => (
                <div key={field.key}>
                  <label className="block text-xs font-medium mb-1" style={{ color: '#6B7280' }}>
                    {field.label}
                    {field.type === 'currency' && <span className="ml-1 text-[10px]">(in INR)</span>}
                    {field.type === 'percentage' && <span className="ml-1 text-[10px]">(%)</span>}
                  </label>
                  <input
                    data-testid={`field-${field.key}`}
                    type="number"
                    step={field.type === 'percentage' ? '0.1' : '1'}
                    value={quantitative[field.key] ?? ''}
                    onChange={e => handleQuantChange(field.key, e.target.value)}
                    className="w-full px-3 py-2.5 rounded-md border text-sm focus:outline-none focus:ring-2 focus:ring-[#2A7B88]"
                    style={{ borderColor: '#E5E1DC', color: '#1B2A4A' }}
                    placeholder={field.type === 'currency' ? 'e.g. 500000' : field.type === 'percentage' ? 'e.g. 95' : '0'}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Qualitative Fields */}
          <div>
            <h3 className="text-sm font-semibold mb-3 pb-2 border-b" style={{ color: '#1B2A4A', borderColor: '#E5E1DC' }}>
              Commentary
            </h3>
            <div className="space-y-4">
              {qualFields.map(field => {
                if (field.type === 'dropdown') {
                  return (
                    <div key={field.key}>
                      <label className="block text-xs font-medium mb-1" style={{ color: '#6B7280' }}>{field.label}</label>
                      <select
                        data-testid={`field-${field.key}`}
                        value={qualitative[field.key] ?? ''}
                        onChange={e => handleQualChange(field.key, e.target.value)}
                        className="w-full px-3 py-2.5 rounded-md border text-sm focus:outline-none focus:ring-2 focus:ring-[#2A7B88]"
                        style={{ borderColor: '#E5E1DC', color: '#1B2A4A' }}
                      >
                        <option value="">Select...</option>
                        {field.options?.map(opt => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </div>
                  );
                }
                if (field.type === 'boolean') {
                  return (
                    <div key={field.key} className="flex items-center gap-3">
                      <label className="text-xs font-medium" style={{ color: '#6B7280' }}>{field.label}</label>
                      <div className="flex items-center gap-2">
                        <button type="button" data-testid={`field-${field.key}-yes`}
                          onClick={() => handleQualChange(field.key, true)}
                          className={`px-3 py-1 rounded text-xs font-medium ${qualitative[field.key] === true ? 'text-white' : ''}`}
                          style={{ backgroundColor: qualitative[field.key] === true ? '#2D8B5E' : '#F3F4F6' }}>
                          Yes
                        </button>
                        <button type="button" data-testid={`field-${field.key}-no`}
                          onClick={() => handleQualChange(field.key, false)}
                          className={`px-3 py-1 rounded text-xs font-medium ${qualitative[field.key] === false ? 'text-white' : ''}`}
                          style={{ backgroundColor: qualitative[field.key] === false ? '#D4726A' : '#F3F4F6' }}>
                          No
                        </button>
                      </div>
                    </div>
                  );
                }
                return (
                  <div key={field.key}>
                    <label className="block text-xs font-medium mb-1" style={{ color: '#6B7280' }}>{field.label}</label>
                    <textarea
                      data-testid={`field-${field.key}`}
                      value={qualitative[field.key] ?? ''}
                      onChange={e => handleQualChange(field.key, e.target.value)}
                      maxLength={field.max_length || 500}
                      rows={2}
                      className="w-full px-3 py-2 rounded-md border text-sm focus:outline-none focus:ring-2 focus:ring-[#2A7B88] resize-none"
                      style={{ borderColor: '#E5E1DC', color: '#1B2A4A' }}
                      placeholder="Enter details..."
                    />
                  </div>
                );
              })}
            </div>
          </div>

          {/* Submit */}
          <div className="sticky bottom-4 pt-4">
            <button
              type="submit"
              disabled={submitting}
              data-testid="submit-update-form-btn"
              className="w-full py-3 rounded-md text-sm font-medium text-white disabled:opacity-50 transition-opacity"
              style={{ backgroundColor: '#1B2A4A' }}
            >
              {submitting ? 'Submitting...' : 'Submit Weekly Update'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
